// src/pages/trading-portal/MarketDataIngestion/OptionsChainPage.js
// Options chain data management page with tabbed interface

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Autocomplete,
    Tabs,
    Tab,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    AccountTree as AccountTreeIcon,
    Calculate as CalculateIcon,
    Refresh as RefreshIcon,
    Sync as SyncIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    OptionsChainStatus,
    OptionsChainStatusLabels,
    OptionsChainStatusColors,
    OptionType,
    OptionTypeLabels,
    OptionTypeColors,
} from '../stockTradingEnums';
const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const FEED_SOURCE_LABELS = {
    yahoo_finance: 'Yahoo Finance',
    alpha_vantage: 'Alpha Vantage',
    polygon: 'Polygon.io',
    alpaca: 'Alpaca Markets',
};

const GREEKS_MODEL_LABELS = {
    black_scholes: 'Black-Scholes',
    binomial: 'Binomial',
    monte_carlo: 'Monte Carlo',
};

const OptionsChainPage = () => {
    const navigate = useNavigate();

    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const[canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared state
    const [symbols, setSymbols] = useState([]);
    const [error, setError] = useState(null);

    // ===== Tab 0: Chain Configs =====
    const [configs, setConfigs] = useState([]);
    const [configsLoading, setConfigsLoading] = useState(true);
    const[configsPage, setConfigsPage] = useState(0);
    const [configsRowsPerPage, setConfigsRowsPerPage] = useState(10);
    const [configsTotalCount, setConfigsTotalCount] = useState(0);

    // Config Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const[editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const[configFormData, setConfigFormData] = useState({
        symbol_id: null,
        feed_source: '',
        focus_dte_min: 0,
        focus_dte_max: 45,
        refresh_interval_seconds: 15,
        greeks_model: 'black_scholes',
        status: 'ACTIVE',
    });
    const [configFormErrors, setConfigFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const[syncingConfigId, setSyncingConfigId] = useState(null);

    // ===== Tab 1: Option Contracts =====
    const [contractsSymbol, setContractsSymbol] = useState(null);
    const [contracts, setContracts] = useState([]);
    const [contractsLoading, setContractsLoading] = useState(false);
    const[contractsPage, setContractsPage] = useState(0);
    const [contractsRowsPerPage, setContractsRowsPerPage] = useState(10);
    const [contractsTotalCount, setContractsTotalCount] = useState(0);
    const [contractsOptionType, setContractsOptionType] = useState('');
    const[contractsExpDate, setContractsExpDate] = useState('');
    const [contractsStrikeMin, setContractsStrikeMin] = useState('');
    const [contractsStrikeMax, setContractsStrikeMax] = useState('');
    const [debouncedStrikeMin, setDebouncedStrikeMin] = useState('');
    const [debouncedStrikeMax, setDebouncedStrikeMax] = useState('');
    const strikeDebounceRef = useRef(null);
    const [calculatingGreeks, setCalculatingGreeks] = useState(null);

    // ===== Tab 2: Open Interest =====
    const [oiSymbol, setOiSymbol] = useState(null);
    const [oiSnapshots, setOiSnapshots] = useState([]);
    const[oiLoading, setOiLoading] = useState(false);
    const [oiPage, setOiPage] = useState(0);
    const [oiRowsPerPage, setOiRowsPerPage] = useState(25);
    const [oiTotalCount, setOiTotalCount] = useState(0);

    // Permission check
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-mdi-options-chain', 'view');
            const add = await hasPermission('tr-mdi-options-chain', 'add');
            const edit = await hasPermission('tr-mdi-options-chain', 'edit');
            const del = await hasPermission('tr-mdi-options-chain', 'delete');
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPermissions();
    },[]);

    // Fetch symbols for Autocomplete
    const fetchSymbols = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setSymbols(response.data.data ||[]);
            }
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchSymbols();
    },[fetchSymbols, canView]);

    // ==================== TAB 0: CHAIN CONFIGS ====================

    const fetchConfigs = useCallback(async () => {
        if (!canView) return;
        setConfigsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({ page: configsPage + 1, per_page: configsRowsPerPage });
            const response = await axiosInstance.get(`/api/market-data-ingestion/options-chain/configs?${params.toString()}`);

            if (response.data.success) {
                setConfigs(response.data.data ||[]);
                setConfigsTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch chain configs');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch chain configs');
        } finally {
            setConfigsLoading(false);
        }
    }, [canView, configsPage, configsRowsPerPage]);

    useEffect(() => {
        if (canView) fetchConfigs();
    },[fetchConfigs, canView]);

    const resetConfigForm = () => {
        setConfigFormData({
            symbol_id: null,
            feed_source: '',
            focus_dte_min: 0,
            focus_dte_max: 45,
            refresh_interval_seconds: 15,
            greeks_model: 'black_scholes',
            status: 'ACTIVE',
        });
        setConfigFormErrors({});
    };

    const handleCreateOpen = () => { resetConfigForm(); setCreateDialogOpen(true); };
    const handleCreateClose = () => { setCreateDialogOpen(false); resetConfigForm(); };

    const handleEditOpen = (config) => {
        setSelectedConfig(config);
        const matchedSymbol = symbols.find(s => s.id === config.symbol_id) || null;
        setConfigFormData({
            symbol_id: matchedSymbol,
            feed_source: config.feed_source || '',
            focus_dte_min: config.focus_dte_min ?? 0,
            focus_dte_max: config.focus_dte_max ?? 45,
            refresh_interval_seconds: config.refresh_interval_seconds ?? 15,
            greeks_model: config.greeks_model || 'black_scholes',
            status: config.status || 'ACTIVE',
        });
        setConfigFormErrors({});
        setEditDialogOpen(true);
    };

    const handleEditClose = () => { setEditDialogOpen(false); setSelectedConfig(null); resetConfigForm(); };

    const handleConfigFormChange = (field) => (event) => {
        setConfigFormData(prev => ({ ...prev, [field]: event.target.value }));
        if (configFormErrors[field]) setConfigFormErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateConfigForm = () => {
        const errors = {};
        if (!configFormData.symbol_id) errors.symbol_id = 'Symbol is required';
        if (!configFormData.feed_source.trim()) errors.feed_source = 'Feed source is required';
        setConfigFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateSubmit = async () => {
        if (!validateConfigForm()) return;
        setSubmitting(true);

        try {
            const payload = {
                symbol_id: configFormData.symbol_id?.id || configFormData.symbol_id,
                feed_source: configFormData.feed_source,
                focus_dte_min: parseInt(configFormData.focus_dte_min, 10),
                focus_dte_max: parseInt(configFormData.focus_dte_max, 10),
                refresh_interval_seconds: parseInt(configFormData.refresh_interval_seconds, 10),
                greeks_model: configFormData.greeks_model,
                status: configFormData.status,
            };
            const response = await axiosInstance.post('/api/market-data-ingestion/options-chain/configs', payload);

            if (response.data.success) {
                handleCreateClose();
                fetchConfigs();
            } else {
                setConfigFormErrors({ submit: response.data.message || 'Failed to create config' });
            }
        } catch (err) {
            setConfigFormErrors({ submit: err.response?.data?.message || 'Failed to create config' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSubmit = async () => {
        if (!validateConfigForm() || !selectedConfig) return;
        setSubmitting(true);

        try {
            const payload = {
                symbol_id: configFormData.symbol_id?.id || configFormData.symbol_id,
                feed_source: configFormData.feed_source,
                focus_dte_min: parseInt(configFormData.focus_dte_min, 10),
                focus_dte_max: parseInt(configFormData.focus_dte_max, 10),
                refresh_interval_seconds: parseInt(configFormData.refresh_interval_seconds, 10),
                greeks_model: configFormData.greeks_model,
                status: configFormData.status,
            };
            const response = await axiosInstance.put(`/api/market-data-ingestion/options-chain/configs/${selectedConfig.id}`, payload);

            if (response.data.success) {
                handleEditClose();
                fetchConfigs();
            } else {
                setConfigFormErrors({ submit: response.data.message || 'Failed to update config' });
            }
        } catch (err) {
            setConfigFormErrors({ submit: err.response?.data?.message || 'Failed to update config' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (config) => { setSelectedConfig(config); setDeleteDialogOpen(true); };

    const handleDeleteConfirm = async () => {
        if (!selectedConfig) return;
        try {
            const response = await axiosInstance.delete(`/api/market-data-ingestion/options-chain/configs/${selectedConfig.id}`);
            if (response.data.success) {
                fetchConfigs();
                setDeleteDialogOpen(false);
                setSelectedConfig(null);
            } else {
                setError(response.data.message || 'Failed to delete config');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete config');
        }
    };

    const handleSyncConfig = async (configId) => {
        setSyncingConfigId(configId);
        setError(null);
        try {
            const response = await axiosInstance.post(`/api/market-data-ingestion/options-chain/sync/${configId}`);
            if (response.data.success) {
                fetchConfigs();
            } else {
                setError(response.data.message || 'Sync failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Sync failed');
        } finally {
            setSyncingConfigId(null);
        }
    };

    // ==================== TAB 1: OPTION CONTRACTS ====================

    // Debounce strike filter values (500ms delay)
    useEffect(() => {
        if (strikeDebounceRef.current) clearTimeout(strikeDebounceRef.current);
        strikeDebounceRef.current = setTimeout(() => {
            setDebouncedStrikeMin(contractsStrikeMin);
            setDebouncedStrikeMax(contractsStrikeMax);
            setContractsPage(0);
        }, 500);
        return () => clearTimeout(strikeDebounceRef.current);
    }, [contractsStrikeMin, contractsStrikeMax]);

    const fetchContracts = useCallback(async () => {
        if (!canView || !contractsSymbol) return;
        setContractsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({ page: contractsPage + 1, per_page: contractsRowsPerPage });
            if (contractsOptionType) params.append('option_type', contractsOptionType);
            if (contractsExpDate) params.append('expiration_date', contractsExpDate);
            if (debouncedStrikeMin !== '') params.append('strike_min', debouncedStrikeMin);
            if (debouncedStrikeMax !== '') params.append('strike_max', debouncedStrikeMax);

            const response = await axiosInstance.get(`/api/market-data-ingestion/options-chain/contracts/${contractsSymbol.id}?${params.toString()}`);

            if (response.data.success) {
                setContracts(response.data.data ||[]);
                setContractsTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch contracts');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch contracts');
        } finally {
            setContractsLoading(false);
        }
    },[canView, contractsSymbol, contractsPage, contractsRowsPerPage, contractsOptionType, contractsExpDate, debouncedStrikeMin, debouncedStrikeMax]);

    // Auto-select first config's symbol when switching to Option Contracts tab
    useEffect(() => {
        if (activeTab === 1 && !contractsSymbol && configs.length > 0 && symbols.length > 0) {
            const matchedSymbol = symbols.find(s => s.id === configs[0].symbol_id);
            if (matchedSymbol) setContractsSymbol(matchedSymbol);
        }
    }, [activeTab, contractsSymbol, configs, symbols]);

    useEffect(() => {
        if (canView && activeTab === 1 && contractsSymbol) fetchContracts();
    },[fetchContracts, canView, activeTab, contractsSymbol]);

    const handleCalculateGreeks = async (contractId) => {
        setCalculatingGreeks(contractId);
        try {
            await axiosInstance.post(`/api/market-data-ingestion/options-chain/calculate-greeks/${contractId}`);
            fetchContracts();
        } catch (err) {
            setError(err.response?.data?.message || 'Greeks calculation failed');
        } finally {
            setCalculatingGreeks(null);
        }
    };

    const [bulkCalculating, setBulkCalculating] = useState(false);

    const handleBulkCalculateGreeks = async () => {
        if (!contractsSymbol) return;
        setBulkCalculating(true);
        setError(null);
        try {
            const res = await axiosInstance.post(`/api/market-data-ingestion/options-chain/bulk-calculate-greeks/${contractsSymbol.id}`);
            if (res.data.success) {
                setError(null);
                fetchContracts();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Bulk Greeks calculation failed');
        } finally {
            setBulkCalculating(false);
        }
    };

    const handleLongCallOpen = () => {
        if (!contractsSymbol) return;
        navigate(`/trading-portal/market-data-ingestion/options-chain/single-option-payoff/${contractsSymbol.id}`, {
            state: { optionType: 'CALL', symbolName: contractsSymbol.symbol },
        });
    };

    const handleLongPutOpen = () => {
        if (!contractsSymbol) return;
        navigate(`/trading-portal/market-data-ingestion/options-chain/single-option-payoff/${contractsSymbol.id}`, {
            state: { optionType: 'PUT', symbolName: contractsSymbol.symbol },
        });
    };

    // ==================== TAB 2: OPEN INTEREST ====================

    const fetchOiSnapshots = useCallback(async () => {
        if (!canView || !oiSymbol) return;
        setOiLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({ page: oiPage + 1, per_page: oiRowsPerPage });
            const response = await axiosInstance.get(`/api/market-data-ingestion/options-chain/oi-snapshots/${oiSymbol.id}?${params.toString()}`);

            if (response.data.success) {
                setOiSnapshots(response.data.data ||[]);
                setOiTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch OI snapshots');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch OI snapshots');
        } finally {
            setOiLoading(false);
        }
    }, [canView, oiSymbol, oiPage, oiRowsPerPage]);

    // Auto-select first config's symbol when switching to Open Interest tab
    useEffect(() => {
        if (activeTab === 2 && !oiSymbol && configs.length > 0 && symbols.length > 0) {
            const matchedSymbol = symbols.find(s => s.id === configs[0].symbol_id);
            if (matchedSymbol) setOiSymbol(matchedSymbol);
        }
    },[activeTab, oiSymbol, configs, symbols]);

    useEffect(() => {
        if (canView && activeTab === 2 && oiSymbol) fetchOiSnapshots();
    },[fetchOiSnapshots, canView, activeTab, oiSymbol]);

    const [takingSnapshot, setTakingSnapshot] = useState(false);

    const handleTakeOiSnapshot = async () => {
        if (!oiSymbol) return;
        setTakingSnapshot(true);
        setError(null);
        try {
            const res = await axiosInstance.post(`/api/market-data-ingestion/options-chain/oi-snapshots/${oiSymbol.id}/take`);
            if (res.data.success) {
                fetchOiSnapshots();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to take OI snapshot');
        } finally {
            setTakingSnapshot(false);
        }
    };

    // ==================== FORMAT HELPERS ====================

    const formatPrice = (price) => price != null ? `$${parseFloat(price).toFixed(2)}` : '-';
    const formatGreek = (val) => val != null ? parseFloat(val).toFixed(4) : '-';
    const formatIV = (iv) => iv != null ? `${(parseFloat(iv) * 100).toFixed(2)}%` : '-';
    const formatNumber = (num) => num != null ? parseInt(num).toLocaleString() : '-';

    // ==================== PERMISSION GATE ====================

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view options chain data.</Alert>
            </Box>
        );
    }

    // ==================== CONFIG FORM FIELDS ====================

    const renderConfigFormFields = () => (
        <Grid container spacing={2} sx={{ mt: 1 }}>
            {configFormErrors.submit && (
                <Grid item xs={12}>
                    <Alert severity="error">{configFormErrors.submit}</Alert>
                </Grid>
            )}
            <Grid item xs={12}>
                <Autocomplete
                    options={symbols}
                    getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                    value={configFormData.symbol_id}
                    onChange={(e, newValue) => {
                        setConfigFormData(prev => ({ ...prev, symbol_id: newValue }));
                        if (configFormErrors.symbol_id) setConfigFormErrors(prev => ({ ...prev, symbol_id: null }));
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Symbol"
                            error={!!configFormErrors.symbol_id}
                            helperText={configFormErrors.symbol_id}
                            required
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth error={!!configFormErrors.feed_source} required>
                    <InputLabel>Feed Source</InputLabel>
                    <Select
                        value={configFormData.feed_source}
                        onChange={handleConfigFormChange('feed_source')}
                        label="Feed Source"
                    >
                        <MenuItem value="yahoo_finance">Yahoo Finance</MenuItem>
                        <MenuItem value="alpha_vantage">Alpha Vantage</MenuItem>
                        <MenuItem value="polygon">Polygon.io</MenuItem>
                        <MenuItem value="alpaca">Alpaca Markets</MenuItem>
                    </Select>
                    {configFormErrors.feed_source && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {configFormErrors.feed_source}
                        </Typography>
                    )}
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label="Focus DTE Min"
                    type="number"
                    value={configFormData.focus_dte_min}
                    onChange={handleConfigFormChange('focus_dte_min')}
                    inputProps={{ min: 0 }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label="Focus DTE Max"
                    type="number"
                    value={configFormData.focus_dte_max}
                    onChange={handleConfigFormChange('focus_dte_max')}
                    inputProps={{ min: 0 }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label="Refresh Interval (seconds)"
                    type="number"
                    value={configFormData.refresh_interval_seconds}
                    onChange={handleConfigFormChange('refresh_interval_seconds')}
                    inputProps={{ min: 1 }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                    <InputLabel>Greeks Model</InputLabel>
                    <Select
                        value={configFormData.greeks_model}
                        onChange={handleConfigFormChange('greeks_model')}
                        label="Greeks Model"
                    >
                        <MenuItem value="black_scholes">Black-Scholes</MenuItem>
                        <MenuItem value="binomial">Binomial</MenuItem>
                        <MenuItem value="monte_carlo">Monte Carlo</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={configFormData.status} label="Status" onChange={handleConfigFormChange('status')}>
                        {Object.values(OptionsChainStatus).map(status => (
                            <MenuItem key={status} value={status}>{OptionsChainStatusLabels[status]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    );

    // ==================== TAB 0 RENDER: CHAIN CONFIGS ====================

    const renderChainConfigs = () => (
        <>
            {/* Action Bar */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                {canAdd && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                        Add Config
                    </Button>
                )}
            </Box>

            {/* Configs Table */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Symbol</TableCell>
                                <TableCell>Feed Source</TableCell>
                                <TableCell>DTE Range</TableCell>
                                <TableCell>Refresh Interval</TableCell>
                                <TableCell>Greeks Model</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Last Sync</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {configsLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                </TableRow>
                            ) : configs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <AccountTreeIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                        <Typography color="text.secondary">No chain configs configured</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                configs.map((config) => (
                                    <TableRow key={config.id} hover>
                                        <TableCell>
                                            <Typography fontWeight="bold">{config.symbol || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={FEED_SOURCE_LABELS[config.feed_source] || config.feed_source || '-'} size="small" sx={{ fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {config.focus_dte_min ?? 0} - {config.focus_dte_max ?? 45}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {config.refresh_interval_seconds ? `${config.refresh_interval_seconds}s` : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={GREEKS_MODEL_LABELS[config.greeks_model] || config.greeks_model || 'Black-Scholes'} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={OptionsChainStatusLabels[config.status] || config.status || 'Unknown'}
                                                size="small"
                                                color={OptionsChainStatusColors[config.status] || 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {config.last_sync_at ? new Date(config.last_sync_at).toLocaleString() : 'Never'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                <Tooltip title="Sync Now">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleSyncConfig(config.id)}
                                                        disabled={syncingConfigId === config.id}
                                                    >
                                                        {syncingConfigId === config.id ? <CircularProgress size={18} /> : <SyncIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                                {canEdit && (
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleEditOpen(config)}><EditIcon /></IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(config)}><DeleteIcon /></IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={configsTotalCount}
                    page={configsPage}
                    onPageChange={(e, newPage) => setConfigsPage(newPage)}
                    rowsPerPage={configsRowsPerPage}
                    onRowsPerPageChange={(e) => { setConfigsRowsPerPage(parseInt(e.target.value, 10)); setConfigsPage(0); }}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Paper>
        </>
    );

    // ==================== TAB 1 RENDER: OPTION CONTRACTS ====================

    const renderOptionContracts = () => (
        <>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            options={symbols}
                            getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                            value={contractsSymbol}
                            onChange={(e, newValue) => { setContractsSymbol(newValue); setContractsPage(0); }}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Symbol" size="small" required />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Option Type</InputLabel>
                            <Select
                                value={contractsOptionType}
                                label="Option Type"
                                onChange={(e) => { setContractsOptionType(e.target.value); setContractsPage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.values(OptionType).map(type => (
                                    <MenuItem key={type} value={type}>{OptionTypeLabels[type]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Expiration Date"
                            type="date"
                            value={contractsExpDate}
                            onChange={(e) => { setContractsExpDate(e.target.value); setContractsPage(0); }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={1.5}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Strike Min"
                            type="number"
                            value={contractsStrikeMin}
                            onChange={(e) => setContractsStrikeMin(e.target.value)}
                            inputProps={{ min: 0, step: 0.5 }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={1.5}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Strike Max"
                            type="number"
                            value={contractsStrikeMax}
                            onChange={(e) => setContractsStrikeMax(e.target.value)}
                            inputProps={{ min: 0, step: 0.5 }}
                        />
                    </Grid>
                    <Grid item>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchContracts} disabled={!contractsSymbol}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<TrendingUpIcon />}
                            onClick={handleLongCallOpen}
                            disabled={!contractsSymbol}
                            sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                        >
                            Long Call
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<TrendingDownIcon />}
                            onClick={handleLongPutOpen}
                            disabled={!contractsSymbol}
                            sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                        >
                            Long Put
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={bulkCalculating ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
                            onClick={handleBulkCalculateGreeks}
                            disabled={!contractsSymbol || bulkCalculating}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                        >
                            {bulkCalculating ? 'Calculating...' : 'Calculate All Greeks'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Contracts Table */}
            {!contractsSymbol ? (
                <Paper sx={{ py: 6, textAlign: 'center' }}>
                    <AccountTreeIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>Select a Symbol</Typography>
                    <Typography color="text.secondary" variant="body2">
                        Choose a symbol to view option contracts with Greeks and IV data
                    </Typography>
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Strike</TableCell>
                                    <TableCell>Expiration</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Bid</TableCell>
                                    <TableCell align="right">Ask</TableCell>
                                    <TableCell align="right">Last</TableCell>
                                    <TableCell align="right">Volume</TableCell>
                                    <TableCell align="right">OI</TableCell>
                                    <TableCell align="right">IV</TableCell>
                                    <TableCell align="right">Delta</TableCell>
                                    <TableCell align="right">Gamma</TableCell>
                                    <TableCell align="right">Theta</TableCell>
                                    <TableCell align="right">Vega</TableCell>
                                    <TableCell align="right">DTE</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contractsLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={15} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                    </TableRow>
                                ) : contracts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No option contracts found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    contracts.map((contract) => (
                                        <TableRow key={contract.id} hover>
                                            <TableCell>
                                                <Typography fontWeight="medium">{formatPrice(contract.strike_price)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {contract.expiration_date ? new Date(contract.expiration_date).toLocaleDateString() : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={OptionTypeLabels[contract.option_type] || contract.option_type}
                                                    size="small"
                                                    color={OptionTypeColors[contract.option_type] || 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">{formatPrice(contract.bid)}</TableCell>
                                            <TableCell align="right">{formatPrice(contract.ask)}</TableCell>
                                            <TableCell align="right">{formatPrice(contract.last_price)}</TableCell>
                                            <TableCell align="right">{formatNumber(contract.volume)}</TableCell>
                                            <TableCell align="right">{formatNumber(contract.open_interest)}</TableCell>
                                            <TableCell align="right">{formatIV(contract.implied_volatility)}</TableCell>
                                            <TableCell align="right">{formatGreek(contract.delta)}</TableCell>
                                            <TableCell align="right">{formatGreek(contract.gamma)}</TableCell>
                                            <TableCell align="right">{formatGreek(contract.theta)}</TableCell>
                                            <TableCell align="right">{formatGreek(contract.vega)}</TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2">{contract.dte ?? '-'}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                    <Tooltip title="Calculate Greeks">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCalculateGreeks(contract.id)}
                                                            disabled={calculatingGreeks === contract.id}
                                                        >
                                                            {calculatingGreeks === contract.id ? <CircularProgress size={18} /> : <CalculateIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={contractsTotalCount}
                        page={contractsPage}
                        onPageChange={(e, newPage) => setContractsPage(newPage)}
                        rowsPerPage={contractsRowsPerPage}
                        onRowsPerPageChange={(e) => { setContractsRowsPerPage(parseInt(e.target.value, 10)); setContractsPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            )}
        </>
    );

    // ==================== TAB 2 RENDER: OPEN INTEREST ====================

    const renderOpenInterest = () => (
        <>
            {/* Symbol Selector */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            options={symbols}
                            getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                            value={oiSymbol}
                            onChange={(e, newValue) => { setOiSymbol(newValue); setOiPage(0); }}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Symbol" size="small" required />
                            )}
                        />
                    </Grid>
                    <Grid item>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchOiSnapshots} disabled={!oiSymbol}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item sx={{ ml: 'auto' }}>
                        <Button
                            variant="contained"
                            startIcon={takingSnapshot ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
                            onClick={handleTakeOiSnapshot}
                            disabled={!oiSymbol || takingSnapshot}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                        >
                            {takingSnapshot ? 'Taking Snapshot...' : 'Take OI Snapshot'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* OI Snapshots Table */}
            {!oiSymbol ? (
                <Paper sx={{ py: 6, textAlign: 'center' }}>
                    <AccountTreeIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>Select a Symbol</Typography>
                    <Typography color="text.secondary" variant="body2">
                        Choose a symbol to view open interest snapshot history
                    </Typography>
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Strike</TableCell>
                                    <TableCell>Expiration</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">OI</TableCell>
                                    <TableCell align="right">OI Change</TableCell>
                                    <TableCell>Interval (min)</TableCell>
                                    <TableCell>Timestamp</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {oiLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                    </TableRow>
                                ) : oiSnapshots.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No OI snapshots found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    oiSnapshots.map((snapshot, index) => (
                                        <TableRow key={snapshot.id || index} hover>
                                            <TableCell>
                                                <Typography fontWeight="medium">{formatPrice(snapshot.strike_price)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {snapshot.expiration_date ? new Date(snapshot.expiration_date).toLocaleDateString() : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={OptionTypeLabels[snapshot.option_type] || snapshot.option_type}
                                                    size="small"
                                                    color={OptionTypeColors[snapshot.option_type] || 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2">{formatNumber(snapshot.open_interest)}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                    sx={{
                                                        color: snapshot.oi_change > 0
                                                            ? '#10b981'
                                                            : snapshot.oi_change < 0
                                                                ? '#ef4444'
                                                                : 'text.secondary',
                                                    }}
                                                >
                                                    {snapshot.oi_change != null
                                                        ? `${snapshot.oi_change > 0 ? '+' : ''}${parseInt(snapshot.oi_change).toLocaleString()}`
                                                        : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{snapshot.snapshot_interval_minutes ?? '-'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {snapshot.snapshot_timestamp ? new Date(snapshot.snapshot_timestamp).toLocaleString() : '-'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={oiTotalCount}
                        page={oiPage}
                        onPageChange={(e, newPage) => setOiPage(newPage)}
                        rowsPerPage={oiRowsPerPage}
                        onRowsPerPageChange={(e) => { setOiRowsPerPage(parseInt(e.target.value, 10)); setOiPage(0); }}
                        rowsPerPageOptions={[10, 25, 50]}
                    />
                </Paper>
            )}
        </>
    );

    // ==================== MAIN RENDER ====================

    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '16px',
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 2.5, md: 3 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AccountTreeIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Options Chain Data Feed
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            Manage chain configs, view option contracts, and track open interest
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="Chain Configs" />
                    <Tab label="Option Contracts" />
                    <Tab label="Open Interest" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderChainConfigs()}
            {activeTab === 1 && renderOptionContracts()}
            {activeTab === 2 && renderOpenInterest()}

            {/* Create Config Dialog */}
            <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
                <DialogTitle>Add Chain Config</DialogTitle>
                <DialogContent>{renderConfigFormFields()}</DialogContent>
                <DialogActions>
                    <Button onClick={handleCreateClose} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateSubmit} disabled={submitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                        {submitting ? <CircularProgress size={24} /> : 'Create Config'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Config Dialog */}
            <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Config: {selectedConfig?.symbol || ''}</DialogTitle>
                <DialogContent>{renderConfigFormFields()}</DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditSubmit} disabled={submitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                        {submitting ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Chain Config</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the chain config for <strong>{selectedConfig?.symbol}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default OptionsChainPage;